import { useState, useCallback, useEffect, useRef } from 'react';
import { appConfig } from '@/config/app-config';
import { getErrorMessage } from '@/utils/error';
import type { SyncStatusResponse, SyncErrorLog } from '@/types';
import { getSyncStatus, getSyncErrors, syncKendra } from '@/api/client';

interface UseKendraSyncProps {
  dataSourceId?: string;
  pollInterval?: number;
}

/**
 * Kendra同期管理フック
 */
export function useKendraSync({
  dataSourceId = appConfig.kendraDataSourceId,
  pollInterval = 5000,
}: UseKendraSyncProps = {}) {
  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncErrors, setSyncErrors] = useState<SyncErrorLog[]>([]);
  const [isLoadingErrors, setIsLoadingErrors] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ステータスを取得
  // dataSourceId が空でもバックエンドが Index/DataSource を動的解決するため、
  // フロント側で早期リターンせず常に API を呼ぶ。
  const fetchStatus = useCallback(async () => {
    try {
      setIsLoadingStatus(true);
      setError(null);

      const result = await getSyncStatus(dataSourceId);

      if (result.success && result.data) {
        setSyncStatus(result.data);

        const isCurrentlySyncing = ['SYNCING', 'SYNCING_INDEXING'].includes(result.data.status);
        setIsSyncing(isCurrentlySyncing);

        return isCurrentlySyncing;
      } else {
        throw new Error(result.error || 'ステータスの取得に失敗しました');
      }
    } catch (err: unknown) {
      console.error('Failed to fetch sync status:', err);
      setError(getErrorMessage(err, 'エラーが発生しました'));
      return false;
    } finally {
      setIsLoadingStatus(false);
    }
  }, [dataSourceId, isSyncing]);

  // エラーログを取得
  // dataSourceId が空でもバックエンドが動的解決するため、常に API を呼ぶ。
  const fetchSyncErrors = useCallback(async () => {
    try {
      setIsLoadingErrors(true);

      const result = await getSyncErrors(dataSourceId);

      if (result.success && result.data) {
        setSyncErrors(result.data.errors);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch sync errors:', err);
    } finally {
      setIsLoadingErrors(false);
    }
  }, [dataSourceId]);

  // ポーリング開始
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      const stillSyncing = await fetchStatus();
      if (!stillSyncing) {
        stopPolling();
      }
    }, pollInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStatus, pollInterval]);

  // ポーリング停止
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // 同期開始
  // dataSourceId が空でもバックエンドが動的解決するため、常に API を呼ぶ。
  const handleStartSync = useCallback(async (): Promise<boolean> => {
    try {
      setError(null);
      setIsSyncing(true);

      const result = await syncKendra(dataSourceId);

      if (result.success) {
        startPolling();
        return true;
      } else {
        throw new Error(result.error || '同期の開始に失敗しました');
      }
    } catch (err: unknown) {
      console.error('Failed to start sync:', err);
      setError(getErrorMessage(err, 'エラーが発生しました'));
      setIsSyncing(false);
      return false;
    }
  }, [dataSourceId, startPolling]);

  // 初回マウント時にステータスとエラーを取得
  useEffect(() => {
    fetchStatus();
    fetchSyncErrors();

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 同期完了時にエラーを再取得
  useEffect(() => {
    if (syncStatus && !isSyncing && syncStatus.status !== 'SYNCING') {
      fetchSyncErrors();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncStatus?.status, isSyncing, fetchSyncErrors]);

  // ステータスのテキスト表示
  const statusText = (() => {
    if (!syncStatus) return '不明';

    switch (syncStatus.status) {
      case 'SYNCING':
      case 'SYNCING_INDEXING':
        return '同期中...';
      case 'SUCCEEDED':
        return '同期完了';
      case 'FAILED':
        return '同期失敗';
      case 'STOPPED':
        return '同期停止';
      case 'NO_HISTORY':
        return '同期履歴なし';
      default:
        return syncStatus.status;
    }
  })();

  // ステータスの色
  const statusColor = ((): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!syncStatus) return 'default';

    switch (syncStatus.status) {
      case 'SYNCING':
      case 'SYNCING_INDEXING':
        return 'info';
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'STOPPED':
        return 'warning';
      default:
        return 'default';
    }
  })();

  return {
    syncStatus,
    isSyncing,
    isLoadingStatus,
    error,
    statusText,
    statusColor,
    handleStartSync,
    refreshStatus: fetchStatus,
    syncErrors,
    isLoadingErrors,
    refreshSyncErrors: fetchSyncErrors,
  };
}

export default useKendraSync;
